import React, { useEffect, useRef, useState } from 'react';
import FroalaEditor from '../../../common/FroalaEditor';
import TextEditorConfig from './EditorConfig';
import useUpdateTeacherNotes from './useUpdateTeacherNotes';
import useSlideShowStore, { ISlide } from '../../SlideShow/stores/useSlideShowStore';

interface TeacherNotesProps {
  slide: ISlide;
}

interface FroalaInstance {
  html: { get: () => string };
}

const TYPING_TIMEOUT = 500;

const TeacherNotes = ({ slide }: TeacherNotesProps) => {
  const initialNotes = slide.notes || '';
  const [content, setContent] = useState(initialNotes);

  const editorRef = useRef<FroalaInstance | null>(null);
  const lastSavedRef = useRef<string>(initialNotes);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const setHasUnsavedChanges = useSlideShowStore(state => state.setHasUnsavedChanges);
  const saveNotes = useUpdateTeacherNotes();

  const froalaConfig = TextEditorConfig({
    onInput: (currentContent: string) => setHasUnsavedChanges(currentContent !== lastSavedRef.current),
    setEditorInstance: (instance: FroalaInstance) => { editorRef.current = instance; },
  });

  const commit = (text: string) => {
    if (text === lastSavedRef.current) return;
    const previouslySaved = lastSavedRef.current;
    lastSavedRef.current = text;
    saveNotes(slide.id, text)
      .catch(() => { lastSavedRef.current = previouslySaved; });
  };

  useEffect(() => () => {
    clearTimeout(timeoutRef.current);

    const currentContent = editorRef.current?.html.get();
    if (currentContent === undefined || currentContent === lastSavedRef.current) return;

    saveNotes(slide.id, currentContent).catch(() => {});
  }, []);

  const handleModelChange = (text: string) => {
    setContent(text);
    if (text === lastSavedRef.current) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => commit(text), TYPING_TIMEOUT);
  };

  return (
    <FroalaEditor
      customConfig={froalaConfig}
      model={content}
      text={content}
      onModelChange={handleModelChange}
    />
  );
};

export default TeacherNotes;
